import { Filters } from '@/core/collections/filters/filter.interface';
import { http } from '@/core/helpers/http.helper';
import { ApiResource } from '@/core/models/api-resource.interface';
import { LocationQuery } from 'vue-router';
import { Communication } from '../communications/communication.interface';
import { Customer } from './customer.interface';

class CUSTOMER_INCLUDES_CLASS {
   all: Record<'includes' | 'appends', Array<string>> = {
      includes: [
         'company',
         'addresses.customer_address_type',
         'addresses.city.country',
         'addresses.city.province',
         'company.city',
         'company.job_functions',
         'company.job_functions.job_function_category',
         'company.sectorType',
         'company.workSchedules',
         'company.activities',
         'contacts',
         'contacts.phone_numbers',
         'positions',
         'customerType',
         'contacts.salutation',
         'contacts.communication_language',
      ],
      appends: ['scopes'],
   };
   list: Record<'includes' | 'appends', Array<string>> = {
      includes: [
         'company',
         'addresses.customerAddressType',
         'addresses.city.country',
         'addresses.city.province',
         'company.sectorType',
         'customerType',
         'customerSpontaneousApplicationAccess',
      ],
      appends: [],
   };
   dropdown: Record<'includes' | 'appends', Array<string>> = {
      includes: [
         'addresses.customer_address_type',
         'addresses.city.country',
         'addresses.city.province',
         'company',
         'contacts',
         'contacts.salutation',
         'contacts.communication_language',
         'customer_spontaneous_application_access',
         'credit_setting.credit_worthy_status',
      ],
      appends: [],
   };

   constructor() {
      this.check(this.list, 'list');
      this.check(this.dropdown, 'dropdown');
   }

   check(includes: Record<'includes' | 'appends', Array<string>>, name: string): boolean {
      const includesDiff = includes.includes.filter((i) => !this.all.includes.includes(i));
      const appendsDiff = includes.appends.filter((i) => !this.all.appends.includes(i));

      if (includesDiff.length || appendsDiff.length) {
         console.group(`CANDIDATE_INCLUDES_CLASS: ${name}`);
         console.info('Missing includes:', includesDiff);
         console.info('Missing appends:', appendsDiff);
         console.groupEnd();
      }

      return !includesDiff.length && !appendsDiff.length;
   }
}

export const CUSTOMER_INCLUDES = new CUSTOMER_INCLUDES_CLASS();

export const CUSTOMER_LIST_INCLUDES = ['company', 'addresses', 'company.sectorType', 'customerType'];

export const CUSTOMER_DROPDOWN_INCLUDES = ['company', 'contacts', 'contacts.salutation', 'contacts.communication_language'];

export const CUSTOMER_APPENDS = ['scopes'];

export class CustomerService {
   async getCustomers(query: LocationQuery) {
      const response = await http.get<ApiResource<Array<Customer>>>(`/customers`, { params: { ...query } });
      return response.data;
   }

   async getFilteredCustomers(query: LocationQuery, filters: Filters) {
      const response = await http.post<ApiResource<Array<Customer>>>(`/customers/search`, filters, {
         params: { ...query },
      });
      return response.data;
   }

   async autocomplete(search: string) {
      const response = await http.get<ApiResource<Array<string>>>(`/customers/autocomplete`, {
         params: { search, per_page: 25 },
      });
      return response.data;
   }

   async getCustomer(customerId: number, query: LocationQuery) {
      const response = await http.get<ApiResource<Customer>>(`/customers/${customerId}`, { params: { ...query } });
      return response.data;
   }

   async postCustomer(customer: Customer) {
      const response = await http.post<ApiResource<Customer>>(`/customers`, customer);
      return response.data;
   }

   async putCustomer(customer: Customer) {
      const response = await http.put<ApiResource<Customer>>(`/customers/${customer.id}`, customer);
      return response.data.data;
   }

   async patchCustomer(customerId: number, patchCustomerData: { path: string; value: string | number }) {
      const response = await http.patch<ApiResource<Customer>>(`/customers/${customerId}`, {
         [patchCustomerData.path]: patchCustomerData.value,
      });
      return response.data;
   }

   async deleteCustomersBulk(customerIds: Array<number>) {
      const response = await http.delete('/customers', { data: { ids: customerIds } });
      return response.data;
   }

   async call(customerId: number, hitId?: number) {
      const response = await http.get<ApiResource<Communication>>(
         hitId ? `/customers/${customerId}/call/${hitId}` : `/customers/${customerId}/call`,
      );
      return response.data;
   }

   async inviteToVideoRoom(customerId: number, videocallRoomId: number) {
      const response = await http.get<ApiResource<Communication>>(`/customers/${customerId}/video-room/${videocallRoomId}/invite`);
      return response.data;
   }
}
